import { Button, Empty, Segmented, Skeleton } from "antd";
import dayjs, { type Dayjs } from "dayjs";
import type { SchedulePublicAvailableSlot } from "@/api/scheduling/schedule-public.types";
import { colors } from "@/design-system/theme/colors";
import {
  formatSlotTime,
  getVisibleSlots,
  type PublicBookingTimeFormat,
} from "../utils";

interface AvailableSlotsPanelProps {
  selectedDate: Dayjs;
  slots?: SchedulePublicAvailableSlot[];
  isLoading: boolean;
  isReserving?: boolean;
  selectedSlotKey?: string | null;
  timeFormat: PublicBookingTimeFormat;
  /** Workspace brand color used to accent the selected slot. */
  brandColor?: string;
  onTimeFormatChange: (format: PublicBookingTimeFormat) => void;
  onSlotSelect?: (slot: SchedulePublicAvailableSlot) => void;
}

const SlotListSkeleton = () => (
  <div className="space-y-3">
    {Array.from({ length: 8 }, (_, index) => (
      <Skeleton.Button
        key={index}
        active
        block
        className="!h-10 !rounded-xl"
      />
    ))}
  </div>
);

export const AvailableSlotsPanel = ({
  selectedDate,
  slots,
  isLoading,
  isReserving = false,
  selectedSlotKey,
  timeFormat,
  brandColor = colors.primary,
  onTimeFormatChange,
  onSlotSelect,
}: AvailableSlotsPanelProps) => {
  const visibleSlots = getVisibleSlots(slots);

  // Lighten the brand color for the selected slot fill. When the brand color is
  // a valid 6-digit hex we apply a low-alpha tint of the same color; otherwise
  // we fall back to the design-system primary tint.
  const isHex6BrandColor = /^#[0-9a-f]{6}$/i.test(brandColor);
  const selectedSlotBg = isHex6BrandColor
    ? `${brandColor}1F`
    : colors.primaryExtraLight;

  return (
    <aside className="flex min-h-[320px] flex-col px-5 py-5 lg:h-full lg:min-h-0">
      <div className="mb-5 flex items-start justify-between gap-4">
        <h2
          className="text-[18px] font-semibold tracking-normal"
          style={{ color: colors.textDarkest }}
        >
          {selectedDate.format("ddd D")}
        </h2>

        <Segmented
          size="middle"
          value={timeFormat}
          options={[
            { label: "12h", value: "12h" },
            { label: "24h", value: "24h" },
          ]}
          onChange={(value) => onTimeFormatChange(value as PublicBookingTimeFormat)}
          className="!rounded-xl"
          style={{ backgroundColor: colors.surfaceLighter }}
        />
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto pr-1">
        {isLoading ? (
          <SlotListSkeleton />
        ) : visibleSlots.length > 0 ? (
          <div className="space-y-3">
            {visibleSlots.map((slot) => {
              const label = formatSlotTime(slot.startTime, timeFormat);
              const slotKey = `${slot.startTime}-${slot.endTime}`;
              const isSelectedSlot = selectedSlotKey === slotKey;
              return (
                <Button
                  key={slotKey}
                  block
                  size="large"
                  loading={isReserving && isSelectedSlot}
                  disabled={isReserving}
                  onClick={() => onSlotSelect?.(slot)}
                  className="!h-10 !rounded-xl !text-[17px] !font-normal !shadow-sm"
                  style={{
                    backgroundColor: isSelectedSlot
                      ? selectedSlotBg
                      : colors.white,
                    borderColor: isSelectedSlot
                      ? brandColor
                      : colors.surfaceDark,
                    color: isSelectedSlot ? brandColor : colors.textStrong,
                  }}
                >
                  <span
                    className="mr-4 inline-flex h-2.5 w-2.5 rounded-full align-middle"
                    style={{ backgroundColor: colors.teal }}
                  />
                  {label}
                </Button>
              );
            })}
          </div>
        ) : (
          <div className="flex h-full items-center justify-center">
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={`No times available on ${selectedDate.format("MMM D")}`}
            />
          </div>
        )}
      </div>

      <div className="mt-5 text-center text-xs" style={{ color: colors.textWeak }}>
        {dayjs().format("YYYY")}
      </div>
    </aside>
  );
};
